import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { getDashboardStats, getBranches, getAttendanceRecords } from '../services/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const getEmployeeStatusLabel = (status) => {
  const labels = {
    active: 'Activo',
    inactive: 'Inactivo',
    on_leave: 'De permiso',
  };
  return labels[status] || status;
};

const kindCount = (agg, kind) =>
  Array.isArray(agg) ? agg.find((x) => x._id === kind)?.count || 0 : 0;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [monthChartData, setMonthChartData] = useState([]);

  useEffect(() => {
    loadStats();
    loadBranches();
  }, []);

  useEffect(() => {
    loadMonthChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedBranch]);

  const loadStats = async () => {
    try {
      const { data } = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    }
  };

  const loadMonthChart = async () => {
    try {
      const [year, month] = selectedMonth.split('-').map((x) => parseInt(x, 10));
      const rangeStart = startOfMonth(new Date(year, month - 1));
      const rangeEnd = endOfMonth(new Date(year, month - 1));
      const startStr = format(rangeStart, 'yyyy-MM-dd');
      const endStr = format(rangeEnd, 'yyyy-MM-dd');

      const { data: rows } = await getAttendanceRecords({
        startDate: startStr,
        endDate: endStr,
        limit: 15000,
      });

      let filtered = rows;
      if (selectedBranch !== 'all') {
        filtered = rows.filter(
          (r) => r.employee?.branch?._id && r.employee.branch._id.toString() === selectedBranch
        );
      }

      const entradas = filtered.filter((r) => r.kind === 'entrada').length;
      const salidas = filtered.filter((r) => r.kind === 'salida').length;

      setMonthChartData([
        { name: 'Entradas', cantidad: entradas },
        { name: 'Salidas', cantidad: salidas },
      ]);
    } catch (error) {
      console.error('Error al cargar gráfica del mes:', error);
    }
  };

  if (!stats) {
    return <Typography>Cargando...</Typography>;
  }

  const att = stats.attendance || {};

  const employeesByStatusData = stats.employees.byStatus.map((item) => ({
    name: getEmployeeStatusLabel(item._id),
    value: item.count,
  }));

  const monthKindPie = [
    { name: 'Entradas', value: att.monthEntradas ?? kindCount(att.monthByKind, 'entrada') },
    { name: 'Salidas', value: att.monthSalidas ?? kindCount(att.monthByKind, 'salida') },
  ].filter((x) => x.value > 0);

  return (
    <Container maxWidth="lg" sx={{ pb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Panel de Administración
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Estadísticas y reportes globales de recursos humanos
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Empleados
              </Typography>
              <Typography variant="h3">{stats.employees.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Sucursales
              </Typography>
              <Typography variant="h3">{stats.employees.byBranch.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Solicitudes Pendientes
              </Typography>
              <Typography variant="h3">
                {stats.requests.byStatus.find((s) => s._id === 'pending')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Asistencia (mes en curso)
              </Typography>
              <Typography variant="h4">{att.monthTotal ?? 0}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Entradas: <strong>{att.monthEntradas ?? kindCount(att.monthByKind, 'entrada')}</strong>
                {' · '}
                Salidas: <strong>{att.monthSalidas ?? kindCount(att.monthByKind, 'salida')}</strong>
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Hoy: {att.todayEmployees ?? 0} empleados · {att.todayEntradas ?? 0} entradas ·{' '}
                {att.todaySalidas ?? 0} salidas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Empleados por Sucursal
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.employees.byBranch}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Empleados" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Estado laboral del personal
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={employeesByStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {employeesByStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Sucursal"
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">Todas</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch._id} value={branch._id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="month"
            label="Mes"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        </Box>

        <Typography variant="h6" gutterBottom>
          Asistencia del mes seleccionado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Entradas y salidas registradas (tablet, encargado o administrador).
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Mes en curso (global)
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={monthKindPie.length ? monthKindPie : [{ name: 'Sin datos', value: 1 }]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => (value ? `${name}: ${value}` : name)}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(monthKindPie.length ? monthKindPie : [{ name: 'Sin datos' }]).map(
                    (entry, index) => (
                      <Cell key={`cell-global-${index}`} fill={COLORS[index % COLORS.length]} />
                    )
                  )}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Filtro aplicado (mes / sucursal)
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="cantidad" fill="#03447a" name="Registros" />
              </BarChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Solicitudes Recientes
        </Typography>
        {stats.requests.pending.length > 0 ? (
          stats.requests.pending.map((request) => (
            <Box
              key={request._id}
              sx={{
                p: 2,
                mb: 1,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              }}
            >
              <Typography variant="body1">
                <strong>
                  {request.employee.firstName} {request.employee.lastName}
                </strong>{' '}
                - {request.type}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(request.startDate), 'dd/MM/yyyy')} -{' '}
                {format(new Date(request.endDate), 'dd/MM/yyyy')}
              </Typography>
              <Typography variant="body2">{request.reason}</Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No hay solicitudes pendientes
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default AdminDashboard;
